// useUploadProcess — registers ONE upload batch as a `document-upload` process in
// ProcessProvider so the global "Procesy" monitor and the app-level
// UploadProgressNotice can show it. Not visual. Generic: items are (key, label).
import { useCallback } from "react";
import { useProcesses, type Process } from "@ds/actions/useProcesses";
import { errorText } from "@shared/errorText";

/** Process `type` every tracked upload registers under — the notice counts by it. */
export const UPLOAD_PROCESS_TYPE = "document-upload";

/** Monitor row text when a failure carries no readable message. */
const UNKNOWN_ERROR = "Nieznany błąd";

export interface UploadItem { key: string; label: string }
export type UploadItemStatus = "success" | "error";

export interface MarkUploadItem {
  (key: string, status: UploadItemStatus, message?: string): void;
  /** Server accepted the file and returned a job it keeps working on: the item stays
   *  "processing" (showing `message`) until a watcher resolves the job. */
  (key: string, status: "accepted", job: { jobId: string; message?: string }): void;
}

export interface TrackUploadOptions {
  /** Process card title — callers pass t("documents.upload.process_label"). */
  label: string;
  /** One per upload unit; the notice counts the ones still in flight. */
  items: UploadItem[];
  /** Secondary line in the monitor, e.g. the transaction reference. */
  description?: string;
}

export type TrackUpload = <T>(
  opts: TrackUploadOptions,
  run: (mark: MarkUploadItem) => Promise<T>,
) => Promise<T>;

/** One item per file: key `${index}:${file.name}` (unique even for duplicate names), label = file name. */
export function uploadItemsFromFiles(files: File[]): UploadItem[] {
  return files.map((f, i) => ({ key: `${i}:${f.name}`, label: f.name }));
}

/** Items still in flight across every RUNNING `document-upload` process. Pure. An item
 *  waiting on a server job is not "in flight" — it doesn't hold the notice or the
 *  tab-close warning open, a watcher resolves it independently. */
export function countInFlightUploads(processes: Process[]): number {
  let n = 0;
  for (const p of processes) {
    if (p.type !== UPLOAD_PROCESS_TYPE || p.status !== "running") continue;
    n += p.items.filter((i) => (i.status === "pending" || i.status === "processing") && !i.jobId).length;
  }
  return n;
}

export function useUploadProcess(): TrackUpload {
  const { startProcess, updateItem, completeProcess, awaitJob, settleProcess } = useProcesses();
  return useCallback(async <T,>(opts: TrackUploadOptions, run: (mark: MarkUploadItem) => Promise<T>) => {
    const { label, items, description } = opts;
    if (items.length === 0) return run(() => {});
    const pid = startProcess(
      UPLOAD_PROCESS_TYPE, label,
      items.map((i) => ({ id: i.key, label: i.label, status: "processing" as const })),
      undefined, description,
    );
    const known = new Set(items.map((i) => i.key));
    const marked = new Set<string>();
    let anyAccepted = false;
    const mark = ((
      key: string,
      status: UploadItemStatus | "accepted",
      arg?: string | { jobId: string; message?: string },
    ) => {
      if (!known.has(key)) return;
      marked.add(key);
      if (status === "accepted") {
        anyAccepted = true;
        const job = arg as { jobId: string; message?: string };
        awaitJob(pid, key, job.jobId, job.message);
        return;
      }
      const message = arg as string | undefined;
      // An error row always carries text — `errorText(e)` may be undefined.
      updateItem(pid, key, status, status === "error" ? (message ?? UNKNOWN_ERROR) : message);
    }) as MarkUploadItem;
    const unmarked = () => items.filter((i) => !marked.has(i.key));
    try {
      const result = await run(mark);
      for (const i of unmarked()) updateItem(pid, i.key, "success");
      return result;
    } catch (e) {
      const msg = errorText(e) ?? UNKNOWN_ERROR;
      for (const i of unmarked()) updateItem(pid, i.key, "error", msg);
      throw e;
    } finally {
      // An accepted item keeps working server-side after this batch's client-side work is
      // done — settleProcess only completes the process once no item is still waiting.
      if (anyAccepted) settleProcess(pid);
      else completeProcess(pid);
    }
  }, [startProcess, updateItem, completeProcess, awaitJob, settleProcess]);
}
